/**
 * SparkFlow — the wizard shell. Holds the progress bar, the
 * per-step content card, and wires step advance/back into the
 * shared Zustand store.
 */

import { useTranslation } from 'react-i18next';

import { ProgressBar } from './ProgressBar';
import { Step1Basic } from './steps/Step1Basic';
import { Step2Inputs } from './steps/Step2Inputs';
import { Step3Outputs } from './steps/Step3Outputs';
import { Step4Parameters } from './steps/Step4Parameters';
import { Step5Logic } from './steps/Step5Logic';
import { Step6Preview } from './steps/Step6Preview';
import { Step7Sign } from './steps/Step7Sign';
import { TOTAL_STEPS, useSparkStore, type WizardStep } from './store';

export function SparkFlow() {
  const { t } = useTranslation('spark');
  const step = useSparkStore((s) => s.currentStep);
  const setStep = useSparkStore((s) => s.setStep);

  const advance = () => {
    if (step < TOTAL_STEPS) setStep((step + 1) as WizardStep);
  };
  const back = () => {
    if (step > 1) setStep((step - 1) as WizardStep);
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
      </header>

      <ProgressBar step={step} />

      <section
        aria-label={t(`steps.${step}.label`)}
        className="rounded-xl border border-border bg-surface p-6 shadow-subtle"
      >
        <StepHeading step={step} />
        <div className="mt-5">{renderStep(step, advance, back)}</div>
      </section>
    </div>
  );
}

function StepHeading({ step }: { step: WizardStep }) {
  const { t } = useTranslation('spark');
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
        {t('progress.step_of', { current: step, total: TOTAL_STEPS })}
      </p>
      <h2 className="mt-1 text-lg font-semibold text-ink">
        {t(`steps.${step}.title`)}
      </h2>
    </div>
  );
}

function renderStep(step: WizardStep, onAdvance: () => void, onBack: () => void) {
  switch (step) {
    case 1:
      return <Step1Basic onAdvance={onAdvance} />;
    case 2:
      return <Step2Inputs onAdvance={onAdvance} onBack={onBack} />;
    case 3:
      return <Step3Outputs onAdvance={onAdvance} onBack={onBack} />;
    case 4:
      return <Step4Parameters onAdvance={onAdvance} onBack={onBack} />;
    case 5:
      return <Step5Logic onAdvance={onAdvance} onBack={onBack} />;
    case 6:
      return <Step6Preview onAdvance={onAdvance} onBack={onBack} />;
    case 7:
      return <Step7Sign onBack={onBack} />;
  }
}
